import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { UserPlus, Copy, QrCode, Download, Ban, Link2, ShieldCheck, ShieldAlert, Check } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { useSession } from '../../session/SessionProvider';
import { errorText } from '../../lib/errors';
import { Card, CardBody, DataTable, Badge, Button, Modal, Field, Input, Select, Switch, ConfirmDialog, EmptyState } from '../../ui';

const STATUS_TONE = { pending: 'brand', used: 'success', expired: 'neutral', revoked: 'neutral' };
const formatPhone = (p) => (/^380\d{9}$/.test(p) ? `+${p.slice(0, 3)} ${p.slice(3, 5)} ${p.slice(5, 8)} ${p.slice(8, 10)} ${p.slice(10)}` : p);

async function copyText(text, t) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(t('home.copied'));
  } catch {
    toast.error(t('common.error'));
  }
}

/** Приглашения сотрудников компании: режим регистрации, список, ссылка и QR */
export default function InvitesPanel({ tenantId, version = 0, onInvite }) {
  const { t, fmt } = useI18n();
  const { canEdit } = useSession();
  const [data, setData] = useState(null);
  const [savingMode, setSavingMode] = useState(false);
  const [showQr, setShowQr] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `/admin/tenants/${tenantId}/invites`;

  const load = useCallback(() => {
    api.get(base).then((res) => setData(res.data)).catch((err) => toast.error(errorText(err, t('common.error'))));
  }, [base, t]);
  useEffect(() => { load(); }, [load, version]);

  const setMode = async (inviteOnly) => {
    setSavingMode(true);
    try {
      await api.put(`/admin/tenants/${tenantId}/registration-mode`, { mode: inviteOnly ? 'invite' : 'open' });
      setData((d) => ({ ...d, registration_mode: inviteOnly ? 'invite' : 'open' }));
      toast.success(t('common.saved'));
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSavingMode(false);
    }
  };

  const openQr = async (invite) => {
    try {
      const res = await api.get(`${base}/${invite.id}/qr`);
      setShowQr({ ...invite, ...res.data.data });
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`${base}/${revoking.id}`);
      toast.success(t('invites.revoked'));
      setRevoking(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const inviteOnly = data?.registration_mode === 'invite';
  const columns = [
    {
      key: 'phone',
      header: t('employees.employee'),
      render: (i) => (
        <div className="min-w-0">
          <p className="tabular font-medium text-zinc-900 dark:text-zinc-100">{formatPhone(i.phone)}</p>
          {i.name && <p className="truncate text-xs text-zinc-500">{i.name}</p>}
        </div>
      ),
    },
    { key: 'status', header: t('common.status'), render: (i) => <Badge tone={STATUS_TONE[i.status]} dot>{t(`invites.statuses.${i.status}`)}</Badge> },
    {
      key: 'created_at',
      header: t('invites.created'),
      sortable: true,
      hideOnMobile: true,
      render: (i) => <span className="text-zinc-500">{fmt.relative(i.created_at)}{i.created_by ? ` · ${i.created_by}` : ''}</span>,
    },
    {
      key: 'expires_at',
      header: t('invites.validUntil'),
      hideOnMobile: true,
      render: (i) => <span className="tabular text-zinc-500">{i.status === 'used' ? fmt.dateTime(i.used_at) : fmt.date(i.expires_at)}</span>,
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'right',
      render: (i) => (i.status !== 'pending' ? null : (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon-sm" icon={Copy} onClick={() => copyText(i.link, t)} aria-label={t('invites.copyLink')} title={t('invites.copyLink')} />
          <Button variant="ghost" size="icon-sm" icon={QrCode} onClick={() => openQr(i)} aria-label={t('invites.showQr')} title={t('invites.showQr')} />
          {canEdit && <Button variant="ghost" size="icon-sm" icon={Ban} onClick={() => setRevoking(i)} aria-label={t('invites.revoke')} title={t('invites.revoke')} />}
        </div>
      )),
    },
  ];

  return (
    <>
      <Card className="mb-5">
        <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${inviteOnly ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400' : 'bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400'}`}>
              {inviteOnly ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
            </span>
            <div>
              <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('invites.modeTitle')}</p>
              <p className="mt-0.5 max-w-xl text-xs text-zinc-500">{inviteOnly ? t('invites.modeInviteHint') : t('invites.modeOpenHint')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={inviteOnly} onChange={setMode} disabled={!canEdit || savingMode || !data} label={t('invites.modeTitle')} />
            {canEdit && <Button icon={UserPlus} onClick={onInvite}>{t('invites.invite')}</Button>}
          </div>
        </CardBody>
      </Card>

      <DataTable
        columns={columns}
        rows={data?.data || []}
        loading={!data}
        empty={<EmptyState icon={Link2} title={t('invites.empty')} text={t('invites.emptyHint')} action={canEdit && <Button icon={UserPlus} onClick={onInvite}>{t('invites.invite')}</Button>} />}
      />

      <QrModal invite={showQr} onClose={() => setShowQr(null)} />
      <ConfirmDialog
        open={!!revoking}
        onClose={() => setRevoking(null)}
        onConfirm={revoke}
        loading={busy}
        title={t('invites.revokeTitle')}
        text={t('invites.revokeText', { phone: revoking ? formatPhone(revoking.phone) : '' })}
        confirmLabel={t('invites.revoke')}
      />
    </>
  );
}

function LinkAndQr({ link, qr, phone, name }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('common.error'));
    }
  };
  return (
    <div className="space-y-4">
      <div>
        <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-zinc-500">{t('invites.link')}</p>
        <div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800/60">
          <code className="min-w-0 flex-1 truncate font-mono text-xs text-zinc-700 dark:text-zinc-300" title={link}>{link}</code>
          <Button size="sm" variant={copied ? 'secondary' : 'primary'} icon={copied ? Check : Copy} onClick={copy}>{copied ? t('home.copied') : t('home.copy')}</Button>
        </div>
        <p className="mt-1.5 text-xs text-zinc-500">{t('invites.linkHint')}</p>
      </div>
      <div className="flex flex-col items-center gap-3 rounded-xl border border-zinc-200 p-4 dark:border-zinc-700">
        <img src={qr} alt={t('invites.qrAlt')} className="h-48 w-48 rounded-lg bg-white p-1" />
        <p className="text-center text-xs text-zinc-500">{t('invites.qrHint')}</p>
        <a
          href={qr}
          download={`invite-${phone}${name ? `-${name.replace(/[^\p{L}\d]+/gu, '_')}` : ''}.png`}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
        >
          <Download className="h-3.5 w-3.5" />{t('invites.downloadQr')}
        </a>
      </div>
    </div>
  );
}

function QrModal({ invite, onClose }) {
  const { t } = useI18n();
  return (
    <Modal open={!!invite} onClose={onClose} title={t('invites.title')} description={invite ? formatPhone(invite.phone) : ''} size="sm"
      footer={<Button variant="secondary" onClick={onClose}>{t('common.close')}</Button>}>
      {invite && <LinkAndQr link={invite.link} qr={invite.qr} phone={invite.phone} name={invite.name} />}
    </Modal>
  );
}

/** «Запросити співробітника»: номер и имя → ссылка и QR */
export function InviteDialog({ open, tenantId, onClose, onCreated }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ phone: '', name: '', ttl_days: '7' });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setForm({ phone: '', name: '', ttl_days: '7' }); setResult(null); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post(`/admin/tenants/${tenantId}/invites`, {
        phone: form.phone, ...(form.name.trim() ? { name: form.name.trim() } : {}), ttl_days: Number(form.ttl_days),
      });
      setResult(res.data.data);
      onCreated?.();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={result ? t('invites.readyTitle') : t('invites.inviteTitle')}
      description={result ? formatPhone(result.phone) : t('invites.inviteHint')}
      size="sm"
      footer={result ? (
        <>
          <Button variant="secondary" onClick={() => { setResult(null); setForm({ phone: '', name: '', ttl_days: form.ttl_days }); }}>{t('invites.another')}</Button>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </>
      ) : (
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="invite-form" loading={saving} icon={Link2}>{t('invites.create')}</Button>
        </>
      )}
    >
      {result ? (
        <LinkAndQr link={result.link} qr={result.qr} phone={result.phone} name={result.name} />
      ) : (
        <form id="invite-form" onSubmit={submit} className="space-y-4">
          <Field label={t('employees.phone')} required hint={t('invites.phoneHint')}>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} inputMode="tel" placeholder="380 67 123 45 67" required autoFocus className="tabular" />
          </Field>
          <Field label={t('invites.name')}>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={100} placeholder={t('invites.namePlaceholder')} />
          </Field>
          <Field label={t('invites.validity')}>
            <Select value={form.ttl_days} onChange={(e) => setForm({ ...form, ttl_days: e.target.value })}>
              {['1', '3', '7', '14', '30'].map((d) => <option key={d} value={d}>{t(`invites.days.${d}`)}</option>)}
            </Select>
          </Field>
        </form>
      )}
    </Modal>
  );
}
